import { Result, err, fold, ok } from '../result'
import {
  Attachment,
  Chat,
  Contact,
  MessageReceivedEvent,
  SentMessage,
  UnreadCount,
} from './types'

export interface DecodeError {
  readonly detail: string
}

type Decoder<T> = (raw: unknown) => Result<T, DecodeError>

function isRecord(raw: unknown): raw is Record<string, unknown> {
  return typeof raw === 'object' && raw !== null
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined
}

function optionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

export const decodeChat: Decoder<Chat> = raw => {
  if (!isRecord(raw)) {
    return err({ detail: 'chat: not an object' })
  }
  if (typeof raw.id !== 'string' || !raw.id) {
    return err({ detail: 'chat: missing id' })
  }
  if (typeof raw.isGroup !== 'boolean') {
    return err({ detail: `chat ${raw.id}: missing isGroup` })
  }
  const memberIds = Array.isArray(raw.memberIds)
    ? raw.memberIds.filter((m): m is string => typeof m === 'string')
    : []

  return ok({
    id: raw.id,
    isGroup: raw.isGroup,
    name: optionalString(raw.name) || '',
    avatar: optionalString(raw.avatar),
    memberIds,
    createdAt: optionalNumber(raw.createdAt) ?? 0,
    createdBy: optionalString(raw.createdBy) || '',
    lastMessageId: optionalString(raw.lastMessageId),
    isArchived: raw.isArchived === true,
  })
}

export const decodeMessage: Decoder<SentMessage> = raw => {
  if (!isRecord(raw)) {
    return err({ detail: 'message: not an object' })
  }
  if (typeof raw.id !== 'string' || !raw.id) {
    return err({ detail: 'message: missing id' })
  }
  if (typeof raw.channelId !== 'string' || !raw.channelId) {
    return err({ detail: `message ${raw.id}: missing channelId` })
  }
  if (typeof raw.authorId !== 'string' || !raw.authorId) {
    return err({ detail: `message ${raw.id}: missing authorId` })
  }
  const createdAt = optionalNumber(raw.createdAt)
  if (createdAt === undefined) {
    return err({ detail: `message ${raw.id}: missing createdAt` })
  }

  return ok({
    state: 'sent',
    id: raw.id,
    channelId: raw.channelId,
    authorId: raw.authorId,
    createdAt,
    text: typeof raw.text === 'string' ? raw.text : '',
    attachments: decodeAttachments(raw.attachments),
    fakeId: optionalString(raw.fakeId),
  })
}

function decodeAttachments(raw: unknown): Attachment[] {
  if (!Array.isArray(raw)) {
    return []
  }

  return raw.reduce<Attachment[]>((acc, item) => {
    if (
      isRecord(item) &&
      typeof item.url === 'string' &&
      item.url &&
      typeof item.fileName === 'string'
    ) {
      acc.push({
        url: item.url,
        fileName: item.fileName,
        contentType: optionalString(item.contentType),
        size: optionalNumber(item.size),
        width: optionalNumber(item.width),
        height: optionalNumber(item.height),
      })
    }
    return acc
  }, [])
}

export const decodeContact: Decoder<Contact> = raw => {
  if (!isRecord(raw)) {
    return err({ detail: 'contact: not an object' })
  }
  if (typeof raw.id !== 'string' || !raw.id) {
    return err({ detail: 'contact: missing id' })
  }

  return ok({
    id: raw.id,
    fullName: optionalString(raw.fullName) || optionalString(raw.email) || '',
    firstName: optionalString(raw.firstName),
    lastName: optionalString(raw.lastName),
    avatarUrl: optionalString(raw.avatarUrl),
    initials: optionalString(raw.initials),
    email: optionalString(raw.email),
    globalRole: optionalString(raw.globalRole),
  })
}

export const decodeUnreadCount: Decoder<UnreadCount> = raw => {
  if (!isRecord(raw)) {
    return err({ detail: 'unread count: not an object' })
  }
  if (typeof raw.channelId !== 'string' || !raw.channelId) {
    return err({ detail: 'unread count: missing channelId' })
  }
  const count = optionalNumber(raw.count)
  if (count === undefined || count < 0) {
    return err({ detail: `unread count ${raw.channelId}: missing count` })
  }

  return ok({ channelId: raw.channelId, count })
}

export const decodeMessageReceivedEvent: Decoder<
  MessageReceivedEvent
> = raw => {
  if (!isRecord(raw)) {
    return err({ detail: 'message_received: not an object' })
  }
  return fold<
    SentMessage,
    DecodeError,
    Result<MessageReceivedEvent, DecodeError>
  >(
    decodeMessage(raw.message),
    message =>
      ok({
        message,
        channelName: optionalString(raw.channelName),
      }),
    error => err({ detail: `message_received: ${error.detail}` }),
  )
}

export function decodeArray<T>(
  raw: unknown,
  decoder: Decoder<T>,
): { values: T[]; errors: DecodeError[] } {
  if (!Array.isArray(raw)) {
    return { values: [], errors: [{ detail: 'expected an array' }] }
  }

  const values: T[] = []
  const errors: DecodeError[] = []
  raw.forEach(item => {
    fold(
      decoder(item),
      value => values.push(value),
      error => errors.push(error),
    )
  })

  return { values, errors }
}

export function decodeStringArray(raw: unknown): string[] {
  return Array.isArray(raw)
    ? raw.filter((item): item is string => typeof item === 'string')
    : []
}
